// client/src/Dealers.jsx
import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import "./Style.css";
import GameSessionHeader from "./GameSessionHeader";
import { formatTransactionCode, RAKE_CODES, TIP_CODES, DEALER_PAYOUT_CODES } from "./utils/transactionUtils";

import {
  FaCheck,
  FaTimes,
  FaInfo,
  FaRegTrashAlt,
  FaDollarSign,
  FaBell,
  FaMobileAlt,
} from "react-icons/fa";

export default function Dealers() {
  const { cardroom } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
    const permission = localStorage.getItem("permission");
  const dealerMode = Number(localStorage.getItem("dealer_mode") || 0);

  // ============================================================================
  // CORE STATE
  // ============================================================================
  const [dealers, setDealers] = useState([]);
  const [wallets, setWallets] = useState([]);
  const [currSession, setCurrSession] = useState(null);
  const [payouts, setPayouts] = useState(null);
  const [staff, setStaff] = useState([]);
  const [newStaffName, setNewStaffName] = useState("");
  
  // ============================================================================
  // UI STATE
  // ============================================================================
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [staffSearchTerm, setStaffSearchTerm] = useState("");
  const [showPayouts, setShowPayouts] = useState(false);
  const [mobileInviteDealer, setMobileInviteDealer] = useState(null);
  const [mobileInviteEmail, setMobileInviteEmail] = useState("");
  const [mobileInviteError, setMobileInviteError] = useState("");
  const [mobileInviteSuccess, setMobileInviteSuccess] = useState("");
  const [mobileInviteSubmitting, setMobileInviteSubmitting] = useState(false);

  // ============================================================================
  // FORM STATE - Add Dealer
  // ============================================================================
  const [newDealerName, setNewDealerName] = useState("");

  // ============================================================================
  // MODAL STATE - Pay Dealer
  // ============================================================================
  const [payingDealer, setPayingDealer] = useState(null);
  const [payingStaff, setPayingStaff] = useState(null);
  const [payAmount, setPayAmount] = useState("");
  const [payMethod, setPayMethod] = useState("");
  const [payReceiver, setPayReceiver] = useState("");
  const [paySplits, setPaySplits] = useState([]);
  const payoutAmountInputRef = useRef(null);

  // ============================================================================
  // MODAL STATE - Transaction History
  // ============================================================================
  const [selectedDealerId, setSelectedDealerId] = useState(null);
  const [selectedDealerTransactions, setSelectedDealerTransactions] = useState([]);
  const [dealerTxFilters, setDealerTxFilters] = useState({
    rake: true,
    tips: true,
    payout: true,
  });


  const [selectedStaffId, setSelectedStaffId] = useState(null);
  const [selectedStaffTransactions, setSelectedStaffTransactions] = useState([]);
  const [staffViewMode, setStaffViewMode] = useState("chips");
  // ============================================================================
  // EFFECT: Auth & Axios Interceptor
  // ============================================================================
  useEffect(() => {
    if (dealerMode === 2) {
      navigate(`/${cardroom}/dealer_dashboard`);
      return;
    }

    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          localStorage.removeItem("token");
          navigate(`/${cardroom}/`);
        }
        return Promise.reject(error);
      }
    );

    return () => axios.interceptors.response.eject(interceptor);
  }, [cardroom, dealerMode, navigate]);

  // ============================================================================
  // EFFECT: Initial Data Load
  // ============================================================================
  useEffect(() => {
    if (!token) {
      navigate(`/`);
      return;
    }

    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;

    async function loadData() {
      try {
        const { data } = await axios.get(`/${cardroom}/api/cardroom`);
        setCurrSession(data.active);

        const { data: allStaff } = await axios.get(`/${cardroom}/api/service_staff`);
        setStaff(allStaff);

        const { data: allDealers } = await axios.get(`/${cardroom}/api/dealers`);
        setDealers(allDealers);

        const { data: allWallets } = await axios.get(`/${cardroom}/api/wallets`);
        setWallets(allWallets);
      } catch (err) {
        console.error("Load data failed", err);
        navigate(`/`);
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, [token, cardroom, navigate]);

  // ============================================================================
  // EFFECT: Prevent body scroll when modal is open
  // ============================================================================
  const isAnyModalOpen =
    selectedDealerId !== null ||
    payingDealer !== null ||
    payingStaff !== null ||
    selectedStaffId !== null ||
    mobileInviteDealer !== null;

  useEffect(() => {
    document.body.style.overflow = isAnyModalOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isAnyModalOpen]);

  useEffect(() => {
    if (!payingDealer && !payingStaff) return;
    requestAnimationFrame(() => {
      payoutAmountInputRef.current?.focus();
      payoutAmountInputRef.current?.select();
    });
  }, [payingDealer, payingStaff]);

  const preventNumberInputWheel = (e) => {
    e.preventDefault();
    e.currentTarget.blur();
  };

  // ============================================================================
  // HANDLERS: Dealer Management
  // ============================================================================
  const addDealer = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    if (!newDealerName.trim()) {
      alert("Enter a name");
      return;
    }

    try {
      setIsSubmitting(true);
      const { data: dealer } = await axios.post(`/${cardroom}/api/dealers`, {
        name: newDealerName.trim(),
      });
      setDealers((d) => [...d, dealer]);
      setNewDealerName("");
    } catch (err) {
      alert(err.response?.data?.error || "Could not add dealer");
    } finally {
      setIsSubmitting(false);
    }
  };

  // ============================================================================
  // HANDLERS: Pay Dealer
  // ============================================================================
  const handlePayDealer = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    try {
      setIsSubmitting(true);
      let payload;

      if (payMethod === "mixed") {
        payload = {
          dealerId: payingDealer.id,
          method: "mixed",
          splits: (paySplits || [])
            .filter((s) => Number(s.amount) > 0 && s.method)
            .map((s) => ({
              amount: Number(s.amount),
              method: s.method,
              receiverId: s.receiver || null,
            })),
        };
      } else {
        payload = {
          dealerId: payingDealer.id,
          amount: Number(payAmount || 0),
          method: payMethod,
          receiverId: payReceiver || null,
        };
      }

      const { data } = await axios.post(`/${cardroom}/api/dealers/pay`, payload);

      setDealers((prev) =>
        prev.map((d) =>
          d.id === payingDealer.id
            ? { ...d, owed: data?.newTips ?? 0 }
            : d
        )
      );

      if (payMethod === "mixed") {
        // Add each split as a separate payout entry
        const newPayouts = (paySplits || [])
          .filter((s) => Number(s.amount) > 0 && s.method)
          .map((s) => ({
            dealer_name: payingDealer.name,
            amount: Number(s.amount),
            method: s.method,
            completed_at: new Date().toISOString(),
          }));
        setPayouts((prev) => [...newPayouts, ...(prev || [])]);
      } else {
        // Add single payout entry
        const newPayout = {
          dealer_name: payingDealer.name,
          amount: Number(payAmount),
          method: payMethod,
          completed_at: new Date().toISOString(),
        };
        setPayouts((prev) => [newPayout, ...(prev || [])]);
      }


      setPayingDealer(null);
      setPayAmount("");
      setPayMethod("");
      setPayReceiver("");
      setPaySplits([]);
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.error || "Error paying dealer");
    } finally {
      setIsSubmitting(false);
    }
  };

  // ============================================================================
  // HANDLERS: Transaction History
  // ============================================================================
  const listDealerTransactions = async (dealerId) => {
    setSelectedDealerId(dealerId);
    try {
      const { data } = await axios.get(`/${cardroom}/api/dealers/${dealerId}/transactions`);
      setSelectedDealerTransactions(data);
    } catch (err) {
      console.error("List dealer transactions failed", err);
    }
  };


  const listStaffTransactions = async (staffId) => {
    setSelectedStaffId(staffId);
    setStaffViewMode("chips");
    try {
      const { data } = await axios.get(`/${cardroom}/api/service_staff/${staffId}/transactions`);
      setSelectedStaffTransactions(data);
    } catch (err) {
      console.error("List staff transactions failed", err);
    }
  };

  const togglePayouts = async () => {
    const newState = !showPayouts;
    setShowPayouts(newState);

    if (newState && payouts === null) {
      try {
        const { data } = await axios.get(`/${cardroom}/api/dealers_and_staff/payouts`);
        setPayouts(data);
      } catch (err) {
        console.error("Error fetching payouts", err);
        alert("Failed to load payouts");
      }
    }
  };


  const addStaff = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    if (!newStaffName.trim()) {
      alert("Enter a name");
      return;
    }

    try {
      setIsSubmitting(true);
      const { data: staffMember } = await axios.post(`/${cardroom}/api/service_staff`, {
        name: newStaffName.trim(),
      });
      setStaff((s) => [...s, staffMember]);
      setNewStaffName("");
    } catch (err) {
      alert(err.response?.data?.error || "Could not add staff member");
    } finally {
      setIsSubmitting(false);
    }
  };

const handlePayStaff = async (e) => {
  e.preventDefault();
  if (isSubmitting) return;

  try {
    setIsSubmitting(true);
    let payload;

    if (payMethod === "mixed") {
      payload = {
        method: "mixed",
        splits: (paySplits || [])
          .filter((s) => Number(s.amount) > 0 && s.method)
          .map((s) => ({
            amount: Number(s.amount),
            method: s.method,
            receiverId: s.receiver || null,
          })),
      };
    } else {
      payload = {
        amount: Number(payAmount || 0),
        method: payMethod,
        receiverId: payReceiver || null,
      };
    }

    const { data } = await axios.post(`/${cardroom}/api/service_staff/${payingStaff.id}/payout`, payload);

    setStaff((prev) =>
      prev.map((s) =>
        s.id === payingStaff.id
          ? { ...s, owed: data?.newOwed ?? 0 }
          : s
      )
    );

    // Add payout tracking
    if (payMethod === "mixed") {
      // Add each split as a separate payout entry
      const newPayouts = (paySplits || [])
        .filter((s) => Number(s.amount) > 0 && s.method)
        .map((s) => ({
          worker_name: payingStaff.name,
          worker_type: 'staff',
          amount: Number(s.amount),
          method: s.method,
          completed_at: new Date().toISOString(),
        }));
      setPayouts((prev) => [...newPayouts, ...(prev || [])]);
    } else {
      // Add single payout entry
      const newPayout = {
        worker_name: payingStaff.name,
        worker_type: 'staff',
        amount: Number(payAmount),
        method: payMethod,
        completed_at: new Date().toISOString(),
      };
      setPayouts((prev) => [newPayout, ...(prev || [])]);
    }

    setPayingStaff(null);
    setPayAmount("");
    setPayMethod("");
    setPayReceiver("");
    setPaySplits([]);
  } catch (err) {
    console.error(err);
    alert(err.response?.data?.error || "Error paying staff");
  } finally {
    setIsSubmitting(false);
  }
};

  const updateDealerMobileState = (updatedDealer) => {
    setDealers((prev) =>
      prev.map((dealer) =>
        dealer.id === updatedDealer.id ? { ...dealer, ...updatedDealer } : dealer
      )
    );
  };

  const openDealerMobileInvite = (dealer) => {
    setMobileInviteDealer(dealer);
    setMobileInviteEmail(dealer.email || "");
    setMobileInviteError("");
    setMobileInviteSuccess("");
  };

  const sendDealerMobileInvite = async (e) => {
    e.preventDefault();
    if (!mobileInviteDealer || mobileInviteSubmitting) return;

    const email = mobileInviteEmail.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setMobileInviteError("Enter a valid email address.");
      return;
    }

    try {
      setMobileInviteSubmitting(true);
      setMobileInviteError("");
      setMobileInviteSuccess("");
      const { data } = await axios.post(
        `/${cardroom}/api/dealers/${mobileInviteDealer.id}/mobile-invite`,
        { email }
      );
      updateDealerMobileState(data);
      setMobileInviteDealer(data);
      setMobileInviteEmail(data.email || "");
      setMobileInviteSuccess(`Invite sent to ${data.email}.`);
    } catch (err) {
      setMobileInviteError(
        err.response?.data?.error || "Failed to send dealer mobile invite."
      );
    } finally {
      setMobileInviteSubmitting(false);
    }
  };

  const cancelDealerMobileInvite = async () => {
    if (!mobileInviteDealer || mobileInviteSubmitting) return;

    try {
      setMobileInviteSubmitting(true);
      setMobileInviteError("");
      setMobileInviteSuccess("");
      const { data } = await axios.delete(
        `/${cardroom}/api/dealers/${mobileInviteDealer.id}/mobile-invite`
      );
      updateDealerMobileState(data);
      setMobileInviteDealer(null);
      setMobileInviteEmail("");
    } catch (err) {
      setMobileInviteError(
        err.response?.data?.error || "Failed to cancel dealer mobile invite."
      );
    } finally {
      setMobileInviteSubmitting(false);
    }
  };

  // ============================================================================
  // COMPUTED VALUES
  // ============================================================================
  const normalizedSearchTerm = searchTerm.trim().toLowerCase();
  const normalizedStaffSearchTerm = staffSearchTerm.trim().toLowerCase();

  const filteredDealers =
    normalizedSearchTerm.length > 0
      ? dealers.filter((d) => d.name.toLowerCase().includes(normalizedSearchTerm))
      : dealers;
  const filteredStaff =
    normalizedStaffSearchTerm.length > 0
      ? staff.filter((s) => s.name.toLowerCase().includes(normalizedStaffSearchTerm))
      : staff;
  const isDealerMobileInvitePending = (dealer) =>
    Number(dealer?.mobile_invite_pending) === 1;
  const isDealerMobileAccountActive = (dealer) =>
    Number(dealer?.mobile_account_active) === 1;
  
      const isDealerNameTaken = dealers.some(
    (d) => d.name.trim().toLowerCase() === newDealerName.trim().toLowerCase()
  );
  const isStaffNameTaken = staff.some(
    (s) => s.name.trim().toLowerCase() === newStaffName.trim().toLowerCase()
  );



  // ============================================================================
  // LOADING STATE
  // ============================================================================

  return (
    <div className="game">
      <GameSessionHeader
        activePage="dealers"
        cardroom={cardroom}
        currSession={currSession}
        dealerMode={dealerMode}
        permission={permission}
        title="Dealer Manager"
      />

      {/* Notifications */}
      {notifications.length > 0 && (
        <div className="game-notifications">
          {notifications.map((n) => (
            <div key={n.id} className="game-notification">
              <FaBell className="game-notification__icon" />
              <span className="game-notification__message">{n.message}</span>
              <button
                className="game-notification__close"
                onClick={() => setNotifications((prev) => prev.filter((x) => x.id !== n.id))}
              >
                <FaTimes />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="game-container">
        {loading ? (
          <div className="loading-container" style={{ padding: "4rem", textAlign: "center", color: "white" }}>
            <h2 style={{ marginBottom: "1.5rem", fontWeight: "700" }}>Gathering Data...</h2>
            <div style={{ display: "flex", justifyContent: "center" }}>
              <div className="spinner" style={{ border: "4px solid rgba(255,255,255,0.1)", borderTop: "4px solid #6366f1", borderRadius: "50%", width: "50px", height: "50px", animation: "spin 1s linear infinite" }}></div>
            </div>
            <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
          </div>
        ) : (
          <>
{/* Session Payouts */}
        <section className="game-section">
          <div 
            style={{ 
              display: "flex", 
              justifyContent: "space-between", 
              alignItems: "center", 
              marginBottom: "1rem",
              cursor: "pointer"  // Add cursor pointer
            }}
            onClick={togglePayouts}  // Add onClick to the whole div
          >
            <h2 className="game-section__title" style={{ margin: 0, paddingBottom: 0, border: "none" }}>
              Session Payouts
            </h2>
            <button 
              className="game-btn game-btn--primary"
              onClick={(e) => {
                e.stopPropagation();  // Prevent double-triggering
                togglePayouts();
              }}
            >
              {showPayouts ? "Hide" : "Show"}
            </button>
          </div>

          {showPayouts && payouts !== null && payouts.length > 0 && (
            <div className="game-transaction-list">
              {payouts.map((p, idx) => (
                <div key={idx} className="game-transaction-item">
                  <span className="game-transaction-time">
                    {new Date(p.completed_at).toLocaleTimeString(undefined, {
                      hour: "numeric",
                      minute: "2-digit",
                    })}
                  </span>
                  <span className={`game-badge ${p.worker_type === 'dealer' ? 'game-badge--running' : 'game-badge--credit'}`}>
                    {p.worker_type === 'dealer' ? 'Dealer' : 'Staff'}
                  </span>
                  <strong className="game-transaction-player">{p.worker_name}</strong>
                  <span className="game-transaction-amount">${parseFloat(p.amount).toFixed(0)}</span>
                  <span className="game-transaction-method">
                    {p.method === "cash"
                      ? p.method
                      :`${p.method}${p.wallet_name ? (parseFloat(p.amount) > 0 ? " to " : " from ") + p.wallet_name : ""}`
                    }
                  </span>
                  <span className="game-transaction-date">
                    {new Date(p.completed_at).toLocaleDateString(undefined, {
                      month: "numeric",
                      day: "numeric",
                    })}
                  </span>
                </div>
              ))}
            </div>
          )}

          {showPayouts && payouts && payouts.length === 0 && ( // Add payouts && check
            <p className="game-empty">No payouts this session</p>
          )}

          {showPayouts && payouts === null && ( // Add loading state
            <p className="game-empty">Loading payouts...</p>
          )}
        </section>


        <div className="game-sidebar-grid">
          <div>
        {/* Add Dealer Section */}
        <section className="game-section">
          <h2 className="game-section__title">Add New Dealer</h2>

          <form className="game-form" onSubmit={addDealer}>
            <input
              className="game-input"
              type="text"
              value={newDealerName}
              onChange={(e) => setNewDealerName(e.target.value)}
              placeholder="Dealer name"
              required
            />

            <button
              type="submit"
              className="game-btn game-btn--primary"
              disabled={isSubmitting || isDealerNameTaken || newDealerName.trim() === ""}
            >
              {isDealerNameTaken ? "Name Taken" : "Add Dealer"}
            </button>
          </form>
        </section>

        {/* Dealers List */}
        <section className="game-section">
          <h2 className="game-section__title">All Dealers ({dealers.length})</h2>

          {/* Search Bar */}
          <div className="game-form" style={{ marginBottom: "1.5rem" }}>
            <input
              className="game-input"
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search dealers"
              style={{ minWidth: "300px" }}
            />
            {searchTerm && (
              <button
                className="game-icon-btn"
                onClick={() => setSearchTerm("")}
                title="Clear search"
              >
                <FaTimes />
              </button>
            )}
          </div>

          {filteredDealers.length === 0 ? (
            <p className="game-empty">
              {searchTerm ? `No dealers match "${searchTerm}"` : "No dealers yet"}
            </p>
          ) : (
            <ul className="game-list">
              {filteredDealers.map((d) => (
                <li key={d.id} className="game-list-item">
                  <div
                    className="game-list-info"
                    onClick={() => listDealerTransactions(d.id)}
                  >
                    <span className="game-list-name">
                      {d.name}
                    </span>

                    <div className="game-player-badges-container">
                      {isDealerMobileInvitePending(d) && (
                        <span className="game-player-badge game-player-badge--credit">
                          Mobile Invite Pending
                        </span>
                      )}

                      {d.owed > 0 && (
                        <span className="game-player-badge game-player-badge--running">
                          Owed ${Math.abs(d.owed)}
                        </span>
                      )}

                      {d.session_tips !== undefined && d.session_tips > 0 && (
                        <span className="game-dealer-tips">
                          {currSession ? "Tips: " : "Last Session Tips: "} ${parseFloat(d.session_tips).toFixed(0)}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="game-player-actions">
                      {!isDealerMobileAccountActive(d) && (
                        <button
                          type="button"
                          className="game-icon-btn game-icon-btn--warning"
                          onClick={(e) => {
                            e.stopPropagation();
                            openDealerMobileInvite(d);
                          }}
                          title={
                            isDealerMobileInvitePending(d)
                              ? "Manage mobile invite"
                              : "Invite dealer to mobile app"
                          }
                          style={{ marginLeft: "0.5rem", verticalAlign: "middle" }}
                        >
                          <FaMobileAlt />
                        </button>
                      )}
                    {d.owed > 0 && (
                      <button
                        className="game-icon-btn game-icon-btn--success"
                        onClick={() => {
                          setPayingDealer(d);
                          setPayAmount(String(d.owed || ""));
                          setPayMethod("");
                          setPayReceiver("");
                          setPaySplits([]);
                        }}
                        title="Pay Dealer"
                      >
                        <FaDollarSign />
                      </button>
                    )}

                    <button
                      className="game-icon-btn game-icon-btn--info"
                      onClick={() => listDealerTransactions(d.id)}
                      title="Dealer History"
                    >
                      <FaInfo />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
        </div>

        <div>
 {/* Add Service Staff Section */}
        <section className="game-section">
          <h2 className="game-section__title">Add Staff</h2>

          <form className="game-form" onSubmit={addStaff}>
            <input
              className="game-input"
              type="text"
              value={newStaffName}
              onChange={(e) => setNewStaffName(e.target.value)}
              placeholder="Staff name"
              required
            />

            <button
              type="submit"
              className="game-btn game-btn--primary"
              disabled={isSubmitting || isStaffNameTaken || newStaffName.trim() === ""}
            >
              {isStaffNameTaken ? "Name Taken" : "Add Staff"}
            </button>
          </form>
        </section>
        {/* Service Staff List */}
        <section className="game-section">
          <h2 className="game-section__title">All Staff ({staff.length})</h2>
          <div className="game-form" style={{ marginBottom: "1.5rem" }}>
            <input
              className="game-input"
              type="text"
              value={staffSearchTerm}
              onChange={(e) => setStaffSearchTerm(e.target.value)}
              placeholder="Search staff"
              style={{ minWidth: "300px" }}
            />
            {searchTerm && (
              <button
                className="game-icon-btn"
                onClick={() => setStaffSearchTerm("")}
                title="Clear search"
              >
                <FaTimes />
              </button>
            )}
          </div>
          {filteredStaff.length === 0 ? (
            <p className="game-empty">
              {staffSearchTerm ? `No staff match "${staffSearchTerm}"` : "No service staff yet"}
            </p>
          ) : (
            <ul className="game-list">
              {filteredStaff.map((s) => (
                <li key={s.id} className="game-list-item">
                  <div
                    className="game-list-info"
                    onClick={() => listStaffTransactions(s.id)}
                  >
                    <span className="game-list-name">{s.name}</span>

                    <div className="game-player-badges-container">
                      {s.owed > 0 && (
                        <span className="game-player-badge game-player-badge--running">
                          Owed ${Math.abs(s.owed)}
                        </span>
                      )}

                      {s.tips !== undefined && s.tips > 0 && (
                        <span className="game-dealer-tips">
                          Tips: ${parseFloat(s.tips).toFixed(0)}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="game-player-actions">
                    {s.owed > 0 && (
                      <button
                        className="game-icon-btn game-icon-btn--success"
                        title="Pay Staff"
                        onClick={() => {
                          setPayingStaff(s);
                          setPayAmount(String(s.owed || ""));
                          setPayMethod("");
                          setPayReceiver("");
                          setPaySplits([]);
                        }}
                      >
                        <FaDollarSign />
                      </button>
                    )}

                    <button
                      className="game-icon-btn game-icon-btn--info"
                      title="Staff History"
                      onClick={() => listStaffTransactions(s.id)}
                    >
                      <FaInfo />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
        </div>
        </div>

        

       

          </>
        )}
      </div>

      {/* ============================================================================ */}
      {/* MODALS */}
      {/* ============================================================================ */}

      {/* Dealer Mobile Invite Modal */}
      {mobileInviteDealer && (
        <>
          <div className="game-modal-overlay" onClick={() => setMobileInviteDealer(null)} />
          <div className="game-modal game-modal--transaction">
            <div className="game-modal__header">
              <h3 className="game-modal__title">Dealer Mobile Access</h3>
              <button
                type="button"
                className="game-icon-btn"
                onClick={() => setMobileInviteDealer(null)}
              >
                <FaTimes />
              </button>
            </div>

            <div className="game-modal__body">
              <div className="dealer-mobile-intro">
                <div className="dealer-mobile-intro__icon">
                  <FaMobileAlt />
                </div>
                <div>
                  <p className="dealer-mobile-intro__eyebrow">
                    {mobileInviteDealer.name}
                  </p>
                  <h4>Invite this dealer to Nexus Mobile</h4>
                  <p>
                    Dealers create their own username and password from the email link.
                    In the app they can view session time, tips, payouts, current balance,
                    and last-session entries. This login does not give access to admin tools.
                  </p>
                </div>
              </div>

              {isDealerMobileInvitePending(mobileInviteDealer) ? (
                <div className="game-form-vertical">
                  <p className="dealer-mobile-note">
                    An invite is currently pending for this dealer. Cancel it to clear the
                    email and token, then send a fresh invite later if needed.
                  </p>
                  <div className="game-player-badges-container">
                    <span className="game-player-badge game-player-badge--credit">
                      Pending invite
                    </span>
                    <span className="game-dealer-tips">{mobileInviteDealer.email}</span>
                  </div>

                  {mobileInviteError && (
                    <p style={{ color: "#ef4444", margin: 0 }}>{mobileInviteError}</p>
                  )}
                  {mobileInviteSuccess && (
                    <p style={{ color: "#22c55e", margin: 0 }}>{mobileInviteSuccess}</p>
                  )}

                  <div className="game-modal__footer">
                    <button
                      type="button"
                      className="game-btn game-btn--ghost"
                      onClick={() => setMobileInviteDealer(null)}
                    >
                      Close
                    </button>
                    <button
                      type="button"
                      className="game-btn game-btn--danger"
                      onClick={cancelDealerMobileInvite}
                      disabled={mobileInviteSubmitting}
                    >
                      {mobileInviteSubmitting ? "Cancelling..." : "Cancel Invite"}
                    </button>
                  </div>
                </div>
              ) : (
                <form className="game-form-vertical" onSubmit={sendDealerMobileInvite}>
                  <p className="dealer-mobile-note">
                    Enter the email address where the dealer should receive their setup link.
                  </p>
                  <input
                    type="email"
                    className="game-input"
                    value={mobileInviteEmail}
                    onChange={(e) => setMobileInviteEmail(e.target.value)}
                    placeholder="dealer@example.com"
                    required
                  />

                  {mobileInviteError && (
                    <p style={{ color: "#ef4444", margin: 0 }}>{mobileInviteError}</p>
                  )}
                  {mobileInviteSuccess && (
                    <p style={{ color: "#22c55e", margin: 0 }}>{mobileInviteSuccess}</p>
                  )}

                  <div className="game-modal__footer">
                    <button
                      type="button"
                      className="game-btn game-btn--ghost"
                      onClick={() => setMobileInviteDealer(null)}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="game-btn game-btn--primary"
                      disabled={mobileInviteSubmitting || !mobileInviteEmail.trim()}
                    >
                      {mobileInviteSubmitting ? "Sending..." : "Send Invite"}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </>
      )}

      {/* Pay Staff Modal */}
      {payingStaff && (
        <>
          <div className="game-modal-overlay" onClick={() => setPayingStaff(null)} />
          <div className="game-modal game-modal--transaction">
            <div className="game-modal__header">
              <h3 className="game-modal__title">Pay {payingStaff.name}</h3>
              <span className="game-badge game-badge--running">Owed ${payingStaff.owed}</span>
            </div>

            <div className="game-modal__body">
              <form className="game-form-vertical" onSubmit={handlePayStaff}>
                {/* Amount */}
                <input
                  ref={payoutAmountInputRef}
                  type="number"
                  className="game-input"
                  value={payAmount}
                  placeholder={`Max $${payingStaff.owed}`}
                  step="1"
                  max={payingStaff.owed}
                  onWheel={preventNumberInputWheel}
                  onChange={(e) => setPayAmount(e.target.value)}
                  required
                />

                {/* Method */}
                <select
                  className="game-input"
                  value={payMethod}
                  onChange={(e) => {
                    const m = e.target.value;
                    setPayMethod(m);
                    if (["cash", "chips", "", "mixed"].includes(m)) {
                      setPayReceiver("");
                    }
                    if (m === "mixed") {
                      setPaySplits((rows) =>
                        rows && rows.length > 0 ? rows : [{ amount: "", method: "", receiver: "" }]
                      );
                    } else {
                      setPaySplits([]);
                    }
                  }}
                  required
                >
                  <option value="" hidden>Method</option>
                  <option value="cash">Cash</option>
                  <option value="venmo">Venmo</option>
                  <option value="cashapp">CashApp</option>
                  <option value="bitcoin">Bitcoin</option>
                  <option value="zelle">Zelle</option>
                  <option value="applePay">Apple Pay</option>
                  <option value="paypal">PayPal</option>
                  <option value="mixed">Mixed</option>
                </select>

                {/* Mixed Splits */}
                {payMethod === "mixed" && (() => {
                  const totalTarget = Math.min(Number(payAmount || 0), Number(payingStaff.owed || 0));
                  const needsWallet = (m) => m && !["cash", "chips"].includes(m);
                  const sumSplits = (paySplits || []).reduce((s, r) => s + (Number(r.amount || 0) || 0), 0);
                  const remaining = totalTarget - sumSplits;

                  const updateSplit = (idx, field, value) => {
                    setPaySplits((arr) => {
                      const next = [...arr];
                      next[idx] = { ...next[idx], [field]: value };
                      if (field === "method" && !needsWallet(value)) next[idx].receiver = "";
                      return next;
                    });
                  };

                  const addSplit = () =>
                    setPaySplits((arr) => [...arr, { amount: "", method: "", receiver: "" }]);

                  const removeSplit = (idx) =>
                    setPaySplits((arr) => {
                      const next = [...arr];
                      next.splice(idx, 1);
                      return next;
                    });

                  return (
                    <div className="game-splits">
                      <div className="game-splits__remaining">
                        <strong>Remaining:</strong> ${remaining}
                        {remaining === 0 && " ✅"}
                      </div>

                      {(paySplits || []).map((row, idx) => (
                        <div key={idx} className="game-split-row">
                          <input
                            type="number"
                            className="game-input"
                            step="1"
                            min="0"
                            placeholder="Amount"
                            value={row.amount}
                            onWheel={preventNumberInputWheel}
                            onChange={(e) => updateSplit(idx, "amount", e.target.value.replace(/[^\d.]/g, ""))}
                          />

                          <select
                            className="game-input"
                            value={row.method || ""}
                            onChange={(e) => updateSplit(idx, "method", e.target.value)}
                          >
                            <option value="" hidden>Method</option>
                            <option value="cash">Cash</option>
                            <option value="venmo">Venmo</option>
                            <option value="cashapp">CashApp</option>
                            <option value="bitcoin">Bitcoin</option>
                            <option value="zelle">Zelle</option>
                            <option value="applePay">Apple Pay</option>
                            <option value="paypal">PayPal</option>
                          </select>

                          <select
                            className="game-input"
                            value={row.receiver || ""}
                            onChange={(e) => updateSplit(idx, "receiver", e.target.value)}
                            disabled={!needsWallet(row.method)}
                          >
                            <option value="" hidden>
                              {needsWallet(row.method) ? "Wallet" : "None"}
                            </option>
                            {wallets.length > 0 ? (
                              wallets.map((w) => (
                                <option key={w.id} value={w.id}>
                                  {w.name}
                                </option>
                              ))
                            ) : (
                              <option disabled>None</option>
                            )}
                          </select>

                          <button
                            type="button"
                            className="game-icon-btn game-icon-btn--danger"
                            onClick={() => removeSplit(idx)}
                          >
                            <FaTimes />
                          </button>
                        </div>
                      ))}

                      <button
                        type="button"
                        className="game-btn game-btn--sm game-btn--ghost"
                        onClick={addSplit}
                        disabled={remaining <= 0}
                      >
                        + Add Split
                      </button>
                    </div>
                  );
                })()}

                {/* Receiver (non-cash/chips/mixed) */}
                {!["cash", "chips", "", "mixed"].includes(payMethod) && (
                  <select
                    className="game-input"
                    value={payReceiver}
                    onChange={(e) => setPayReceiver(e.target.value)}
                    required
                  >
                    <option value="" hidden>From</option>
                    {wallets.length > 0 ? (
                      wallets.map((w) => (
                        <option key={w.id} value={w.id}>
                          {w.name}
                        </option>
                      ))
                    ) : (
                      <option disabled>None</option>
                    )}
                  </select>
                )}

                {/* Actions */}
                <div className="game-modal__footer">
                  <button
                    type="button"
                    className="game-btn game-btn--ghost"
                    onClick={() => setPayingStaff(null)}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="game-btn game-btn--primary"
                    disabled={
                      isSubmitting ||
                      !payAmount ||
                      Number(payAmount) <= 0 ||
                      Number(payAmount) > Number(payingStaff.owed) ||
                      !payMethod ||
                      (payMethod === "mixed" &&
                        (Array.isArray(paySplits) && paySplits.every(r => Number(r?.amount) < 1))) ||
                      (payMethod !== "mixed" &&
                        !["cash", "chips"].includes(payMethod) &&
                        !payReceiver)
                    }
                  >
                    Pay Staff
                  </button>
                </div>
              </form>
            </div>
          </div>
        </>
      )}


      {/* Pay Dealer Modal */}
      {payingDealer && (
        <>
          <div className="game-modal-overlay" onClick={() => setPayingDealer(null)} />
          <div className="game-modal game-modal--transaction">
            <div className="game-modal__header">
              <h3 className="game-modal__title">Pay {payingDealer.name}</h3>
              <span className="game-badge game-badge--running">Owed ${payingDealer.owed}</span>
            </div>

            <div className="game-modal__body">
              <form className="game-form-vertical" onSubmit={handlePayDealer}>
                {/* Amount */}
                <input
                  ref={payoutAmountInputRef}
                  type="number"
                  className="game-input"
                  value={payAmount}
                  placeholder={`Max $${payingDealer.owed}`}
                  step="1"
                  max={payingDealer.owed}
                  onWheel={preventNumberInputWheel}
                  onChange={(e) => setPayAmount(e.target.value)}
                  required
                />

                {/* Method */}
                <select
                  className="game-input"
                  value={payMethod}
                  onChange={(e) => {
                    const m = e.target.value;
                    setPayMethod(m);
                    if (["cash", "chips", "", "mixed"].includes(m)) {
                      setPayReceiver("");
                    }
                    if (m === "mixed") {
                      setPaySplits((rows) =>
                        rows && rows.length > 0 ? rows : [{ amount: "", method: "", receiver: "" }]
                      );
                    } else {
                      setPaySplits([]);
                    }
                  }}
                  required
                >
                  <option value="" hidden>Method</option>
                  <option value="cash">Cash</option>
                  <option value="venmo">Venmo</option>
                  <option value="cashapp">CashApp</option>
                  <option value="bitcoin">Bitcoin</option>
                  <option value="zelle">Zelle</option>
                  <option value="applePay">Apple Pay</option>
                  <option value="paypal">PayPal</option>
                  <option value="mixed">Mixed</option>
                </select>

                {/* Mixed Splits */}
                {payMethod === "mixed" && (() => {
                  const totalTarget = Math.min(Number(payAmount || 0), Number(payingDealer.owed || 0));
                  const needsWallet = (m) => m && !["cash", "chips"].includes(m);
                  const sumSplits = (paySplits || []).reduce((s, r) => s + (Number(r.amount || 0) || 0), 0);
                  const remaining = totalTarget - sumSplits;

                  const updateSplit = (idx, field, value) => {
                    setPaySplits((arr) => {
                      const next = [...arr];
                      next[idx] = { ...next[idx], [field]: value };
                      if (field === "method" && !needsWallet(value)) next[idx].receiver = "";
                      return next;
                    });
                  };

                  const addSplit = () =>
                    setPaySplits((arr) => [...arr, { amount: "", method: "", receiver: "" }]);

                  const removeSplit = (idx) =>
                    setPaySplits((arr) => {
                      const next = [...arr];
                      next.splice(idx, 1);
                      return next;
                    });

                  return (
                    <div className="game-splits">
                      <div className="game-splits__remaining">
                        <strong>Remaining:</strong> ${remaining}
                        {remaining === 0 && " ✅"}
                      </div>

                      {(paySplits || []).map((row, idx) => (
                        <div key={idx} className="game-split-row">
                          <input
                            type="number"
                            className="game-input"
                            step="1"
                            min="0"
                            placeholder="Amount"
                            value={row.amount}
                            onWheel={preventNumberInputWheel}
                            onChange={(e) => updateSplit(idx, "amount", e.target.value.replace(/[^\d.]/g, ""))}
                          />

                          <select
                            className="game-input"
                            value={row.method || ""}
                            onChange={(e) => updateSplit(idx, "method", e.target.value)}
                          >
                            <option value="" hidden>Method</option>
                            <option value="cash">Cash</option>
                            <option value="venmo">Venmo</option>
                            <option value="cashapp">CashApp</option>
                            <option value="bitcoin">Bitcoin</option>
                            <option value="zelle">Zelle</option>
                            <option value="applePay">Apple Pay</option>
                            <option value="paypal">PayPal</option>
                            <option value="chips">Chips</option>
                          </select>

                          <select
                            className="game-input"
                            value={row.receiver || ""}
                            onChange={(e) => updateSplit(idx, "receiver", e.target.value)}
                            disabled={!needsWallet(row.method)}
                          >
                            <option value="" hidden>
                              {needsWallet(row.method) ? "Wallet" : "None"}
                            </option>
                            {wallets.length > 0 ? (
                              wallets.map((w) => (
                                <option key={w.id} value={w.id}>
                                  {w.name}
                                </option>
                              ))
                            ) : (
                              <option disabled>None</option>
                            )}
                          </select>

                          <button
                            type="button"
                            className="game-icon-btn game-icon-btn--danger"
                            onClick={() => removeSplit(idx)}
                          >
                            <FaTimes />
                          </button>
                        </div>
                      ))}

                      <button
                        type="button"
                        className="game-btn game-btn--sm game-btn--ghost"
                        onClick={addSplit}
                        disabled={remaining <= 0}
                      >
                        + Add Split
                      </button>
                    </div>
                  );
                })()}

                {/* Receiver (non-cash/chips/mixed) */}
                {!["cash", "chips", "", "mixed"].includes(payMethod) && (
                  <select
                    className="game-input"
                    value={payReceiver}
                    onChange={(e) => setPayReceiver(e.target.value)}
                    required
                  >
                    <option value="" hidden>From</option>
                    {wallets.length > 0 ? (
                      wallets.map((w) => (
                        <option key={w.id} value={w.id}>
                          {w.name}
                        </option>
                      ))
                    ) : (
                      <option disabled>None</option>
                    )}
                  </select>
                )}

                {/* Actions */}
                <div className="game-modal__footer">
                  <button
                    type="button"
                    className="game-btn game-btn--ghost"
                    onClick={() => setPayingDealer(null)}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="game-btn game-btn--primary"
                    disabled={
                      isSubmitting ||
                      !payAmount ||
                      Number(payAmount) <= 0 ||
                      Number(payAmount) > Number(payingDealer.owed) ||
                      !payMethod ||
                      (payMethod == "mixed" &&
                        (Array.isArray(paySplits) && paySplits.every(r => Number(r?.amount) < 1))) ||
                      (payMethod !== "mixed" &&
                        !["cash", "chips"].includes(payMethod) &&
                        !payReceiver)
                    }
                  >
                    Pay Dealer
                  </button>
                </div>
              </form>
            </div>
          </div>
        </>
      )}

      {/* Staff Transaction History Modal */}
{selectedStaffId !== null && (
  <>
    <div
      className="game-modal-overlay"
      onClick={() => {
        setSelectedStaffId(null);
        setSelectedStaffTransactions([]);
      }}
    />
    <div className="game-modal game-modal--large">
      <div className="game-modal__header">
        <h3 className="game-modal__title">
          {staff.find((s) => s.id === selectedStaffId)?.name}'s Transactions
        </h3>
        <button
          className="game-icon-btn"
          onClick={() => {
            setSelectedStaffId(null);
            setSelectedStaffTransactions([]);
          }}
        >
          <FaTimes />
        </button>
      </div>

      <div className="game-modal__body game-modal__body--scroll">
        {selectedStaffTransactions.length === 0 ? (
          <p className="game-empty">No transactions yet.</p>
        ) : (
          <div className="game-transaction-list">
            {selectedStaffTransactions.map((t) => {
              const amt = parseFloat(t.amount).toFixed(0);
              
              return (
                <div 
                  key={t.id} 
                  className="game-transaction-item"
                  style={[28, 29].includes(t.code) ? {
                    background: "rgba(237, 137, 54, 0.1)",
                    border: "1px solid rgba(237, 137, 54, 0.3)",
                  } : {}}
                >
                  <span className="game-transaction-time">
                    {new Date(t.completed_at).toLocaleTimeString(undefined, {
                      hour: "numeric",
                      minute: "2-digit",
                    })}
                  </span>
                  <span className="game-transaction-amount">${amt}</span>
                  
                  {t.code === 25 ? (
                    <span className="game-transaction-method">credit</span>
                  ) : [26, 27, 28, 29].includes(t.code) ? (
                    <span className="game-transaction-method">
                      {`${t.method}${t.wallet_name ? (parseFloat(t.amount) > 0 ? " to " : " from ") + t.wallet_name : ""}`}
                    </span>
                  ) : null}

                  {[28, 29].includes(t.code) && (
                    <span className="game-badge game-badge--running" style={{ marginLeft: "auto" }}>
                      Payout from Balance
                    </span>
                  )}
                  
                  <span className="game-transaction-date">
                    {new Date(t.completed_at).toLocaleDateString(undefined, {
                      month: "numeric",
                      day: "numeric",
                    })}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  </>
)}



      {/* Dealer Transaction History Modal */}
      {selectedDealerId !== null && (
        <>
          <div
            className="game-modal-overlay"
            onClick={() => {
              setSelectedDealerId(null);
              setSelectedDealerTransactions([]);
            }}
          />
          <div className="game-modal game-modal--large">
            <div className="game-modal__header">
              <h3 className="game-modal__title">
                {dealers.find((d) => d.id === selectedDealerId)?.name}'s Transactions
              </h3>
              <button
                className="game-icon-btn"
                onClick={() => {
                  setSelectedDealerId(null);
                  setSelectedDealerTransactions([]);
                }}
              >
                <FaTimes />
              </button>
            </div>

            <div className="game-modal__filters">
              {[
                { key: "rake", label: "Rake" },
                { key: "tips", label: "Tips" },
                { key: "payout", label: "Payout" },
              ].map(({ key, label }) => (
                <label key={key} className="game-filter-chip">
                  <input
                    type="checkbox"
                    checked={dealerTxFilters[key]}
                    onChange={(e) =>
                      setDealerTxFilters((prev) => ({
                        ...prev,
                        [key]: e.target.checked,
                      }))
                    }
                  />
                  <span>{label}</span>
                </label>
              ))}
              <div className="game-filter-actions">
                <button
                  type="button"
                  className="game-btn game-btn--sm"
                  onClick={() =>
                    setDealerTxFilters({
                      rake: true,
                      tips: true,
                      payout: true,
                    })
                  }
                >
                  All
                </button>
                <button
                  type="button"
                  className="game-btn game-btn--sm"
                  onClick={() =>
                    setDealerTxFilters({
                      rake: false,
                      tips: false,
                      payout: false,
                    })
                  }
                >
                  None
                </button>
              </div>
            </div>

            <div className="game-modal__body game-modal__body--scroll">
              {(() => {
                const filtered = selectedDealerTransactions.filter((t) => {
                  const code = Number(t.code);
                  const isRake = RAKE_CODES.includes(code);
                  const isTip = TIP_CODES.includes(code);
                  const isPayout = DEALER_PAYOUT_CODES.includes(code);

                  if (isRake && dealerTxFilters.rake) return true;
                  if (isTip && dealerTxFilters.tips) return true;
                  if (isPayout && dealerTxFilters.payout) return true;
                  
                  if (!isRake && !isTip && !isPayout) return true;
                  return false;
                });

                if (filtered.length === 0) {
                  return <p className="game-empty">No matching transactions.</p>;
                }

                return (
                  <div className="game-transaction-list">
                    {filtered.map((t) => {
                      const amt = parseFloat(t.amount).toFixed(0);
                      const codeName = formatTransactionCode(t.code);
                      return (
                        <div key={t.id} className="game-transaction-item">
                          <span className="game-transaction-time">
                            {new Date(t.completed_at).toLocaleTimeString(undefined, {
                              hour: "numeric",
                              minute: "2-digit",
                            })}
                          </span>
                          <span className="game-transaction-amount">${amt}</span>
                          <strong className="game-transaction-player">
                            {codeName}
                          </strong>
                          <span className="game-transaction-method">
                            {t.method}
                          </span>
                          <span className="game-transaction-table">
                            {t.name ? t.name : t.table_id ? `Table ID: ${t.table_id}` : ""}
                          </span>
                          <span className="game-transaction-date">
                            {new Date(t.completed_at).toLocaleDateString(undefined, {
                              month: "numeric",
                              day: "numeric",
                            })}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
